import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, gql } from '@apollo/client'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { useMediaBuyingContext } from '../../../../../../context/media-buying-context'
import { CORE_CAMPAIGN_FIELDS } from '@/graphql/campaign-fragments'
import type { AdCampaignEditWizardPrefetchedData } from '@/pages/campaigns/edit/utils/adCampaignEditWizardInitialState'
import { getCampaignObjectiveLabel } from '@/utils/campaign-objectives'

// GraphQL query to get campaign by ID
const GET_CAMPAIGN_BY_ID = gql`
    ${CORE_CAMPAIGN_FIELDS}
    query GetCampaignById($campaignId: ID!) {
        campaigns(where: { id: $campaignId }) {
            ...CoreCampaignFields
        }
    }
`

export interface ReviewLaunchData {
    campaignName: string
    launchOption: string
    launchDate: string
    launchTime: string
    audienceNames: Record<string, string>
}

interface CampaignQueryData {
    campaigns: Array<{ id: string; name?: string }>
}

interface UseCampaignNameReturn {
    isLoading: boolean
    campaignData: CampaignQueryData | undefined
}

/**
 * Hook to auto-populate campaign name using the pattern:
 * [Name Stamp] – Campaign [Campaign Name] – [Campaign Objective]
 */
export function useCampaignName(
    reviewLaunchData: ReviewLaunchData,
    updateReviewLaunchData: (updates: Partial<ReviewLaunchData>) => void,
): UseCampaignNameReturn {
    const { id: campaignId } = useParams<{ id: string }>()
    const { getStepContentData } = useWizard() as {
        getStepContentData: (stepName?: string) => Record<string, unknown> | null
    }
    const { connectedPublishers } = useMediaBuyingContext() as {
        connectedPublishers: Record<string, { label?: string }> | null
    }

    // Check if we have prefetched data in wizard state
    const prefetchedData = (getStepContentData('prefetchedData') || {}) as Partial<AdCampaignEditWizardPrefetchedData>
    const hasPrefetchedData = !!(prefetchedData.campaignName && prefetchedData.publisherNameStamp)
    const isEditAdCampaignFlow =
        prefetchedData.isEditMode === true || prefetchedData.isEditAdSetMode === true
    const isDuplicateFlow = prefetchedData.isDuplicateFlow === true

    // Only fetch campaign data if we don't have prefetched data
    const { data: campaignData, loading: campaignLoading } = useQuery<CampaignQueryData>(GET_CAMPAIGN_BY_ID, {
        variables: { campaignId },
        skip: !campaignId || hasPrefetchedData,
    })

    // Auto-populate campaign name if not set
    useEffect(() => {
        // Only populate if this is the first time opening the step (data is null, undefined, or empty object without campaignName)
        const rawReviewLaunchData = getStepContentData('reviewLaunch') as Record<string, unknown> | null
        if (!rawReviewLaunchData || !rawReviewLaunchData.hasOwnProperty('campaignName')) {
            let campaignName: string | undefined
            let nameStamp: string | undefined

            if (hasPrefetchedData) {
                // Use prefetched data - no loading state needed
                campaignName = prefetchedData.campaignName
                nameStamp = prefetchedData.publisherNameStamp
            } else {
                // Fallback to fetched data if prefetched data is not available
                const campaign = campaignData?.campaigns?.[0]
                campaignName = campaign?.name

                // Get name stamp from connected publishers (use first available)
                const firstPublisher = Object.values(connectedPublishers || {})[0]
                nameStamp = firstPublisher?.label || 'Turba Media'
            }

            // Get objective from wizard state
            const objectiveData = getStepContentData('campaignObjective') as { selectedObjective?: string } | null
            const objectiveId = objectiveData?.selectedObjective || 'awareness'
            const objectiveTitle = getCampaignObjectiveLabel(objectiveId)

            // Construct the name if we have all pieces (create flow only). Edit flow stores the
            // full ad campaign name in prefetchedData — do not wrap it again in the template.
            if (campaignName && (hasPrefetchedData || !campaignLoading)) {
                if (isDuplicateFlow && hasPrefetchedData) {
                    const source =
                        prefetchedData.sourceAdCampaignName ||
                        prefetchedData.originalCampaignName ||
                        prefetchedData.campaignName ||
                        ''
                    if (source && !reviewLaunchData.campaignName) {
                        // Strip trailing date suffixes — the backend appends the current date on creation
                        const cleanName = source.replace(/(?: - \d{4}-\d{2}-\d{2})+$/, '')
                        updateReviewLaunchData({ campaignName: `Clone of ${cleanName}` })
                    }
                } else if (isEditAdCampaignFlow && hasPrefetchedData) {
                    const storedName =
                        prefetchedData.originalCampaignName || prefetchedData.campaignName
                    updateReviewLaunchData({ campaignName: storedName })
                } else {
                    const constructedName = `${nameStamp} – Campaign ${campaignName} – ${objectiveTitle}`
                    updateReviewLaunchData({ campaignName: constructedName })
                }
            }
        }
    }, [
        campaignId,
        campaignData,
        campaignLoading,
        prefetchedData,
        hasPrefetchedData,
        isEditAdCampaignFlow,
        isDuplicateFlow,
        reviewLaunchData.campaignName,
        getStepContentData,
        connectedPublishers,
        updateReviewLaunchData,
    ])

    return {
        isLoading: hasPrefetchedData ? false : campaignLoading,
        campaignData,
    }
}
